// @spec AI-STR-028
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  useAdminConversations,
  useExportConversations,
  useToggleConversationFlag,
} from '@/features/settings/ai/api/use-conversation-review';
import { ConversationReviewFilters } from '@/features/settings/ai/components/ConversationReviewFilters';
import { ConversationReviewTable } from '@/features/settings/ai/components/ConversationReviewTable';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

const searchSchema = z.object({
  search: z.string().optional().default(''),
  userId: z.string().optional().catch(undefined),
  module: z.string().optional().catch(undefined),
  status: z.string().optional().catch(undefined),
  flagged: z
    .enum(['true', 'false'])
    .optional()
    .transform((val) => {
      if (val === 'true') return true;
      if (val === 'false') return false;
      return undefined;
    })
    .catch(undefined),
  sortBy: z.string().optional().default('lastMessageAt'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  page: z.coerce.number().optional().default(1),
  pageSize: z.coerce.number().optional().default(25),
});

export const Route = createFileRoute('/_authenticated/settings/ai-conversations')({
  validateSearch: searchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.aiConversationReview'),
  }),
  component: AIConversationReviewPage,
});

function AIConversationReviewPage() {
  const { t } = useTranslation('settings');
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();
  const navigate = useNavigate({ from: Route.fullPath });
  const searchParams = Route.useSearch();

  const filters = {
    search: searchParams.search || '',
    userId: searchParams.userId ?? null,
    module: searchParams.module ?? null,
    status: searchParams.status ?? null,
    flagged: searchParams.flagged ?? null,
    sortBy: searchParams.sortBy,
    sortOrder: searchParams.sortOrder,
    page: searchParams.page,
    pageSize: searchParams.pageSize,
  };

  const { data, isLoading } = useAdminConversations(filters);
  const toggleFlag = useToggleConversationFlag();
  const { exportCsv } = useExportConversations(filters);

  const updateSearch = React.useCallback(
    (updates: Record<string, unknown>) => {
      void navigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [navigate]
  );

  const handleSearchChange = React.useCallback(
    (search: string) => {
      updateSearch({ search, page: 1 });
    },
    [updateSearch]
  );

  const handleModuleChange = React.useCallback(
    (module: string | null) => {
      updateSearch({ module: module ?? undefined, page: 1 });
    },
    [updateSearch]
  );

  const handleStatusChange = React.useCallback(
    (status: string | null) => {
      updateSearch({ status: status ?? undefined, page: 1 });
    },
    [updateSearch]
  );

  const handleFlaggedChange = React.useCallback(
    (flagged: boolean | null) => {
      updateSearch({
        flagged: flagged === null ? undefined : String(flagged),
        page: 1,
      });
    },
    [updateSearch]
  );

  const handleSortChange = React.useCallback(
    (sortBy: string, sortOrder: 'asc' | 'desc') => {
      updateSearch({ sortBy, sortOrder });
    },
    [updateSearch]
  );

  const handlePageChange = React.useCallback(
    (page: number) => {
      updateSearch({ page });
    },
    [updateSearch]
  );

  const handlePageSizeChange = React.useCallback(
    (pageSize: number) => {
      updateSearch({ pageSize, page: 1 });
    },
    [updateSearch]
  );

  const handleRowClick = React.useCallback(
    (conversationId: string) => {
      void navigate({
        to: '/settings/ai-conversations/$conversationId',
        params: { conversationId },
      });
    },
    [navigate]
  );

  const handleFlagToggle = React.useCallback(
    (conversationId: string, flagged: boolean) => {
      toggleFlag.mutate({ conversationId, flagged });
    },
    [toggleFlag]
  );

  if (isRoleLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl">
          <h1 className="text-3xl font-semibold text-foreground">
            {t('aiConversationReviewPage.accessDeniedTitle')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('aiConversationReviewPage.accessDeniedDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">
              {t('aiConversationReviewPage.title')}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {t('aiConversationReviewPage.description')}
            </p>
          </div>
          <Button variant="outline" onClick={exportCsv}>
            <Download className="mr-2 h-4 w-4" />
            {t('aiConversationReviewPage.exportCsv')}
          </Button>
        </header>

        <ConversationReviewFilters
          search={searchParams.search}
          onSearchChange={handleSearchChange}
          module={searchParams.module ?? null}
          onModuleChange={handleModuleChange}
          status={searchParams.status ?? null}
          onStatusChange={handleStatusChange}
          flagged={searchParams.flagged ?? null}
          onFlaggedChange={handleFlaggedChange}
        />

        <ConversationReviewTable
          conversations={data?.items ?? []}
          pagination={
            data?.pagination ?? {
              page: 1,
              pageSize: 25,
              totalCount: 0,
              totalPages: 1,
              hasNext: false,
              hasPrev: false,
            }
          }
          sortBy={searchParams.sortBy}
          sortOrder={searchParams.sortOrder}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onRowClick={handleRowClick}
          onFlagToggle={handleFlagToggle}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
